<script setup lang="ts">
import { h, ref } from 'vue';
import RexButtonV3 from '@/rex-components/RexButtonV3.vue';
import RexIconV3 from '@/rex-components/RexIconV3.vue';
import AnomalyInfoGraphModal from '@/components/charts/utils/AnomalyInfoGraphModal.vue';
import { replaceSpaceInId } from '../../utils/helper-function';
import { ParamsType, Aggregation } from '@/components/Common/graph.types';

const openModal = ref(false);

interface Props {
  title?: string | undefined;
  handleZoomOut: () => void;
  handleZoomIn: () => void;
  handleReset: () => void;
  displayExpand: boolean;
  metricName?: string;
  params?: ParamsType;
  serviceName?: string;
  axisLabels?: { x: string; y: string };
  aggregation?: Aggregation;
}
const props = withDefaults(defineProps<Props>(), {
  metricName: '',
  axisLabels: () => ({ x: '', y: '' }),
  title: '',
  displayExpand: true,
  serviceName: '',
  params: () => ({
    condition: {
      name: '',
      entity: {},
    },
    duration: {},
  }),
  aggregation: () => ({ req: false }),
});
const aggregation = ref(props.aggregation);
const { title, handleZoomOut, handleReset, handleZoomIn } = props;
</script>

<template>
  <div class="graph-card-action-wrapper">
    <span
      class="graph-card-action-icon"
      @click="handleZoomOut"
      :id="`${replaceSpaceInId(props.metricName)}-zoom-out`"
    >
      <BaseIcon name="zoomOut" width="2.438rem" height="2.438rem" />
    </span>
    <span
      class="graph-card-action-icon"
      @click="handleZoomIn"
      :id="`${replaceSpaceInId(props.metricName)}-zoom-in`"
    >
      <BaseIcon name="zoomIn" width="2.438rem" height="2.438rem" />
    </span>
    <span
      class="graph-card-action-icon"
      @click="handleReset"
      :id="`${replaceSpaceInId(props.metricName)}-zoom-reset`"
    >
      <BaseIcon name="reset" width="1.438rem" height="1.438rem" />
    </span>
    <RexButtonV3
      v-if="displayExpand"
      variant="text"
      :icon="h(RexIconV3, { iconName: 'expand' })"
      class="graph-card-action-btn"
      @onClick="() => (openModal = true)"
      :id="`${replaceSpaceInId(props.metricName)}-open-modal`"
    />

    <AnomalyInfoGraphModal
      v-if="openModal"
      :title="title"
      queryName="queryGetAIMetricValues"
      @onClose="() => (openModal = false)"
      :metricName="metricName"
      :params="params"
      :serviceName="serviceName"
      :axisLabels="axisLabels"
      :aggregation="aggregation"
    />
  </div>
</template>

<style lang="scss" scoped>
.graph-card-action-btn {
  @include flex-center;
  border: none;
}
.graph-card-action-icon {
  fill: $color-brand-accent !important;
}
.graph-card-action-icon:hover {
  cursor: pointer;
  svg {
    fill: $font-secondary !important;
  }
}
.graph-card-action-icon:last-child {
  margin-left: 0.3125em;
}
.graph-card-action-icon:first-child {
  margin-right: -0.3125em;
}
.graph-card-action-wrapper {
  @include flex-center-justify(space-between);
}
</style>
